import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, Loader2 } from 'lucide-react';
import { DragDrop } from '@eduvidu/react-drag-and-drop';
import '@eduvidu/react-drag-and-drop/dist/DragDrop.css';

interface UploadModalProps {
  open: boolean;
  onClose: () => void;

  /**
   * Mode A (original / legacy) — called immediately when files are selected;
   * modal auto-closes. Used by MedicalHistory, CurrentMedicalInformation.
   * All original behaviour, layout and DragDrop props are 100% preserved.
   */
  onFileSelect?: (files: File[]) => void;

  /**
   * Mode B (new) — when provided the modal stays open, shows a footer with
   * Cancel + Upload buttons, and calls this handler when the user clicks Upload.
   * Used by Documents, ImagingStudiesPage.
   */
  onUpload?: (files: File[]) => Promise<void>;

  onValidationError?: (errors: string[]) => void;

  /** Modal header title */
  title?: string;

  /**
   * Optional subtitle shown below the title.
   * Only visible in Mode B; ignored in Mode A.
   */
  subtitle?: string;

  /** Uploading state (both modes) */
  isUploading?: boolean;
  loadingText?: string;

  /** Optional DragDrop theme overrides */
  borderColor?: string;
  backgroundColor?: string;
  activeBorderColor?: string;
  activeBackgroundColor?: string;
  /** Optional content above the drop zone, e.g. a just-in-time consent ask. */
  notice?: React.ReactNode;
}

const UploadModal: React.FC<UploadModalProps> = ({
  open,
  onClose,
  onFileSelect,
  onUpload,
  onValidationError,
  title = 'Upload Documents',
  subtitle,
  isUploading = false,
  loadingText = 'Uploading documents…',
  borderColor = '#035DB8',
  backgroundColor = '#F0F7FF',
  activeBorderColor = '#024D9A',
  activeBackgroundColor = '#D0E8FF',
  notice,
}) => {
  // Internal file state — only used in Mode B
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  const busy = uploading || isUploading;
  const isUploadMode = typeof onUpload === 'function';

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  // Prevent body scroll
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Reset internal state when closed
  useEffect(() => {
    if (!open) setSelectedFiles([]);
  }, [open]);

  if (!open) return null;

  // ── Mode A handler (original behaviour: call onFileSelect then close) ────────
  const handleLegacySelect = (files: File[]) => {
    if (onFileSelect) {
      onFileSelect(files);
      if (!isUploading) onClose();
    }
  };

  // ── Mode B handler (upload button click) ─────────────────────────────────────
  const handleUploadClick = async () => {
    if (!onUpload || !selectedFiles.length || busy) return;
    setUploading(true);
    try {
      await onUpload(selectedFiles);
      setSelectedFiles([]);
      onClose();
    } finally {
      setUploading(false);
    }
  };

  // ── Mode A — EXACT original layout (unchanged) ───────────────────────────────
  if (!isUploadMode) {
    return createPortal(
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
        aria-modal="true"
        role="dialog"
        aria-label={title}
      >
        {/* Original backdrop */}
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity"
          onClick={() => { if (!isUploading) onClose(); }}
        />

        {/* Original panel */}
        <div
          className="relative z-10 w-full max-w-lg bg-white dark:bg-surface-dark-200 rounded-2xl shadow-2xl ring-1 ring-black/[0.06] dark:ring-white/[0.07] flex flex-col overflow-hidden animate-fade-in-up-sm"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Original header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-surface-100 dark:border-white/[0.07]">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-brand-50 dark:bg-brand-500/10 text-brand-500">
              <Upload size={16} />
            </div>
            <h2 className="flex-1 text-ui-base font-semibold text-slate-800 dark:text-slate-100 leading-tight">
              {title}
            </h2>
            <button
              type="button"
              onClick={() => { if (!isUploading) onClose(); }}
              disabled={isUploading}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-surface-100 dark:hover:bg-white/[0.06] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Close upload modal"
            >
              <X size={17} />
            </button>
          </div>

          {/* Original body */}
          <div className="px-5 py-5">
            {notice}
            <DragDrop
              onFileSelect={handleLegacySelect}
              onValidationError={onValidationError}
              maxFiles={500}
              allowFolders={true}
              showFileList={true}
              allowRemove={!isUploading}
              isUploading={isUploading}
              loadingText={loadingText}
              dragText="Drag &amp; drop documents here, or click to browse"
              dragActiveText="Drop files here to attach"
              selectedSingleText="1 file ready to upload"
              selectedMultipleText="{count} files ready to upload"
              borderColor={borderColor}
              backgroundColor={backgroundColor}
              activeBorderColor={activeBorderColor}
              activeBackgroundColor={activeBackgroundColor}
              borderRadius="14px"
              fontFamily="inherit"
            />
          </div>

          {/* Original footer — always shown in Mode A */}
          <div className="px-5 pb-4 flex justify-end">
            <button
              type="button"
              onClick={() => { if (!isUploading) onClose(); }}
              disabled={isUploading}
              className="px-4 py-2 text-ui-sm font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-surface-100 dark:hover:bg-white/[0.06] rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // ── Mode B — new large layout with Upload button ──────────────────────────────
  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-[2vw]"
      aria-modal="true"
      role="dialog"
      aria-label={title}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-md animate-fade-in"
        onClick={() => { if (!busy) onClose(); }}
      />

      {/* Panel */}
      <div
        className="relative flex flex-col w-[70vw] h-[75vh] bg-white dark:bg-surface-dark-50 rounded-2xl shadow-2xl border border-surface-200/40 dark:border-surface-dark-200 overflow-hidden animate-slide-up-fade"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top accent line */}
        <div className="h-[3px] bg-gradient-to-r from-brand-500 via-brand-400 to-cyan-400 shrink-0" />

        {/* Header */}
        <div className="px-6 pt-5 pb-4 flex items-center justify-between shrink-0 border-b border-surface-100/60 dark:border-surface-dark-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center shadow-lg shadow-brand-500/25">
              <Upload size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-body-sm font-bold text-slate-800 dark:text-slate-100">{title}</h3>
              {subtitle && (
                <p className="text-compact-sm text-slate-400 dark:text-slate-300">{subtitle}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => { if (!busy) onClose(); }}
            disabled={busy}
            className="p-2 rounded-xl text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Close upload modal"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 py-5 flex flex-col [&_.dnd-wrapper]:flex-1 [&_.dnd-wrapper]:min-h-0 [&_.dnd-zone]:flex-1 [&_.dnd-zone]:min-h-0">
          {notice && <div className="shrink-0">{notice}</div>}
          <DragDrop
            onFileSelect={setSelectedFiles}
            onValidationError={onValidationError}
            maxFiles={500}
            allowFolders={true}
            showFileList={true}
            allowRemove={!busy}
            isUploading={busy}
            loadingText={loadingText}
            dragText="Drag &amp; drop your files here, or click to browse"
            dragActiveText="Drop files here to attach"
            selectedSingleText="1 file selected — ready to upload"
            selectedMultipleText="{count} files selected — ready to upload"
            icon={<Upload size={28} className="text-brand-500" />}
            borderColor={borderColor}
            backgroundColor={backgroundColor}
            activeBorderColor={activeBorderColor}
            activeBackgroundColor={activeBackgroundColor}
            borderRadius="16px"
            fontFamily="Inter, system-ui, -apple-system, sans-serif"
          />
        </div>

        {/* Footer with Cancel + Upload */}
        <div className="px-6 py-4 flex justify-end gap-2.5 border-t border-surface-100/60 dark:border-surface-dark-200 shrink-0">
          <button
            type="button"
            onClick={() => { if (!busy) onClose(); }}
            disabled={busy}
            className="px-5 py-2.5 text-compact-sm font-medium rounded-xl border border-surface-200 dark:border-surface-dark-200 hover:bg-surface-50 dark:hover:bg-surface-dark-200 text-slate-500 dark:text-slate-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUploadClick}
            disabled={busy || !selectedFiles.length}
            className={`px-6 py-2.5 text-compact-sm font-bold rounded-xl text-white transition-all inline-flex items-center gap-2 ${
              selectedFiles.length && !busy
                ? 'bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 shadow-lg shadow-brand-500/25 hover:shadow-xl active:scale-[0.97]'
                : 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-300 cursor-not-allowed'
            }`}
          >
            {busy
              ? <><Loader2 size={14} className="animate-spin" /> Uploading&hellip;</>
              : <><Upload size={14} strokeWidth={2.5} /> Upload</>
            }
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UploadModal;
